import lodashGet from "lodash/get";
import React, { type ComponentPropsWithoutRef, Suspense } from "react";

import { type PillarTableColumnProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { usePillarTableColumnsValues } from "shared/components/pillar-table/columns/usePillarTableColumnsValues";
import { type PillarTableNestedTableExportLayout } from "shared/components/pillar-table/export/pivotPillarTableNestedRows";
import {
  filterPillarTableDataColumns,
  getPillarTableNestedRows,
} from "shared/components/pillar-table/nested/pillarTableNestedTableHelpers";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { type DeepKeys } from "shared/types/deepkeys";

// Lazy load PillarTable to avoid circular dependency
const PillarTable = React.lazy(
  () => import("shared/components/pillar-table/PillarTable"),
);

export type PillarTableNestedTableProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> = ComponentPropsWithoutRef<"div"> & {
  children:
    | Array<React.ReactElement<PillarTableColumnProps<RowGeneric>>>
    | React.ReactElement<PillarTableColumnProps<RowGeneric>>;
  valuePath: DeepKeys<RowGeneric>;
  pagination?: boolean;
  urlParams?: boolean;
  queryKeyProperty: DeepKeys<RowGeneric>;
  nestedTableColumns?: any[];
  /**
   * Labels this nested table, and names its sheet on export. Worth setting once
   * a row carries more than one nested table, so the reader can tell the
   * certifications from the invoice items.
   */
  heading?: string;
  /**
   * How this nested table is exported. Leave it out for one row per item on a
   * sheet of its own; see `PillarTableNestedTableExportLayout` for columns.
   */
  exportLayout?: PillarTableNestedTableExportLayout;
};

/**
 * One relation of a parent row, rendered as a full-width row underneath it.
 *
 * A row may declare several of these; each one owns only its own `valuePath`
 * and renders nothing when that relation is empty. Whether the row is expanded
 * at all is the row's decision, not this component's — see `PillarTableRow`.
 */
const PillarTableNestedTable = <T,>({
  valuePath,
  children,
  queryKeyProperty,
  heading,
  nestedTableColumns = [],
}: PillarTableNestedTableProps<T>) => {
  const { rowValue, rowExpanded } = usePillarTableRowContext();
  const { columns: parentTableColumns, testidTable: parentTestIdTable } =
    usePillarTableContext();
  const nestedData = getPillarTableNestedRows(rowValue, valuePath);
  const queryKey = rowValue && lodashGet(rowValue, queryKeyProperty);
  const { fixedColumnWidths, columnRefs, columns } =
    usePillarTableColumnsValues({
      children,
      displayColumns: nestedTableColumns,
    });

  if (!nestedData.length || rowExpanded === "none") {
    return <></>;
  }

  return (
    <tr className={`${rowExpanded === "close" ? "hidden" : ""}`}>
      <td colSpan={filterPillarTableDataColumns(parentTableColumns).length + 1}>
        <div
          className="p-2 bg-neutral-light"
          data-testid={`row-${queryKey}-nested-table-${String(valuePath)}`}
        >
          {heading && (
            <div className="pb-0.5 font-semibold text-sm">{heading}</div>
          )}
          <Suspense fallback={<div>Loading...</div>}>
            <PillarTable
              className="w-full"
              pillarTableContext={{
                tableRowResults: nestedData,
                children: children,
                columns: columns,
                fixedColumnWidths: fixedColumnWidths,
                columnRefs: columnRefs,
                loadedFirstTime: true,
                testidTable: `${parentTestIdTable}-nested-table-${String(valuePath)}-${queryKey}`,
              }}
            >
              {children}
            </PillarTable>
          </Suspense>
        </div>
      </td>
    </tr>
  );
};

/**
 * Lets `isPillarTableNestedTable` recognise the JSX form without importing this
 * module, which would close a cycle back through the columns hook.
 */
PillarTableNestedTable.isNestedTable = true;

export default PillarTableNestedTable;
